/**
 * Billing hooks — TanStack Query wrappers for the billing API.
 *
 * All endpoints are under /api/v1/billing and require super_admin or sales_admin role.
 *
 * Hooks:
 * - useAccountInvoices(accountId)         — list invoices for a single account
 * - useClientInvoices(clientId)           — list invoices aggregated across all accounts for a client
 * - useClientBillingRollup(clientId)      — per-account billing summary for a client
 * - useProvisionStripeCustomer(clientId)  — mutation to create Stripe Customer for an account
 * - useCreateSubscription(clientId)       — mutation to create Stripe Subscription for an account
 * - useGeneratePaymentLink()              — mutation to generate a Stripe payment link for an account
 * - useGrantCredit(clientId)             — mutation to grant credit balance to an account
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiGet, apiPost } from '@/lib/api'
import { accountKeys } from './useAccounts'

// ── Response types ────────────────────────────────────────────────────────────

export interface InvoiceRow {
  id: string
  account_id: string
  stripe_invoice_id: string | null
  status: string
  amount_cents: number
  currency: string
  due_date: string | null
  paid_at: string | null
  period_start: string | null
  period_end: string | null
  pdf_url: string | null
  created_at: string
  updated_at: string
}

export interface BillingRollupRow {
  account_id: string
  account_name: string
  stripe_customer_id: string | null
  total_paid_cents: number
  open_invoices: number
  overdue_invoices: number
}

interface InvoicesListResponse {
  data: InvoiceRow[]
}

interface BillingRollupResponse {
  data: BillingRollupRow[]
}

interface StripeCustomerResponse {
  data: { stripe_customer_id: string }
}

interface PaymentLinkResponse {
  data: { url: string }
}

// ── Query key factory ─────────────────────────────────────────────────────────

const billingKeys = {
  all: ['billing'] as const,
  accountInvoices: (accountId: string) =>
    [...billingKeys.all, 'invoices', 'account', accountId] as const,
  clientInvoices: (clientId: string) =>
    [...billingKeys.all, 'invoices', 'client', clientId] as const,
  clientRollup: (clientId: string) => [...billingKeys.all, 'rollup', clientId] as const,
}

// ── Hooks ─────────────────────────────────────────────────────────────────────

/**
 * Fetch all invoices for a single account.
 * Disabled when accountId is falsy.
 */
export function useAccountInvoices(accountId: string) {
  return useQuery<InvoiceRow[]>({
    queryKey: billingKeys.accountInvoices(accountId),
    queryFn: async () => {
      const res = await apiGet<InvoicesListResponse>(
        `/api/v1/billing/accounts/${accountId}/invoices`,
      )
      return res.data
    },
    enabled: !!accountId,
  })
}

/**
 * Fetch all invoices aggregated across all accounts for a client.
 * Disabled when clientId is falsy.
 */
export function useClientInvoices(clientId: string) {
  return useQuery<InvoiceRow[]>({
    queryKey: billingKeys.clientInvoices(clientId),
    queryFn: async () => {
      const res = await apiGet<InvoicesListResponse>(
        `/api/v1/billing/clients/${clientId}/invoices`,
      )
      return res.data
    },
    enabled: !!clientId,
  })
}

/**
 * Fetch the per-account billing rollup summary for a client.
 * Returns real data from the billing-rollup API endpoint.
 * Disabled when clientId is falsy.
 */
export function useClientBillingRollup(clientId: string) {
  return useQuery<BillingRollupRow[]>({
    queryKey: billingKeys.clientRollup(clientId),
    queryFn: async () => {
      const res = await apiGet<BillingRollupResponse>(
        `/api/v1/billing/clients/${clientId}/billing-rollup`,
      )
      return res.data
    },
    enabled: !!clientId,
  })
}

/**
 * Mutation to provision a Stripe Customer for a specific account.
 * On success: invalidates account list for the parent client AND billing rollup.
 */
export function useProvisionStripeCustomer(clientId: string) {
  const queryClient = useQueryClient()

  return useMutation<{ stripe_customer_id: string }, Error, string>({
    mutationFn: async (accountId: string) => {
      const res = await apiPost<StripeCustomerResponse>(
        `/api/v1/billing/accounts/${accountId}/stripe-customer`,
        {},
      )
      return res.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: accountKeys.byClient(clientId) })
      queryClient.invalidateQueries({ queryKey: billingKeys.clientRollup(clientId) })
    },
  })
}

/**
 * Mutation to create a Stripe Subscription for a specific account.
 * On success: invalidates account list for the parent client.
 */
export function useCreateSubscription(clientId: string) {
  const queryClient = useQueryClient()

  return useMutation<unknown, Error, string>({
    mutationFn: async (accountId: string) => {
      const res = await apiPost<{ data: unknown }>(
        `/api/v1/billing/accounts/${accountId}/subscription`,
        {},
      )
      return res.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: accountKeys.byClient(clientId) })
    },
  })
}

/**
 * Mutation to generate a Stripe payment link for a specific account.
 * Returns { url: string } — the caller extracts the URL.
 */
export function useGeneratePaymentLink() {
  return useMutation<{ url: string }, Error, string>({
    mutationFn: async (accountId: string) => {
      const res = await apiPost<PaymentLinkResponse>(
        `/api/v1/billing/accounts/${accountId}/payment-link`,
        {},
      )
      return res.data
    },
  })
}

/**
 * Mutation to grant credit balance to a specific account.
 * Body: { accountId, amountCents, currency, description }
 * On success: invalidates the billing rollup for the parent client.
 */
export function useGrantCredit(clientId: string) {
  const queryClient = useQueryClient()

  return useMutation<
    unknown,
    Error,
    { accountId: string; amountCents: number; currency: string; description: string }
  >({
    mutationFn: async ({ accountId, amountCents, currency, description }) => {
      const res = await apiPost<{ data: unknown }>(
        `/api/v1/billing/accounts/${accountId}/credit`,
        { amountCents, currency, description },
      )
      return res.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: billingKeys.clientRollup(clientId) })
    },
  })
}
